import { httpStatusOfError, InternalId } from '@edgebox/data-definition-kit';
import { ContentItemStatus, ContentOptimizationStatus } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ClientContentOptimizationEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { loadPageOptimizations } from './suggestedFaqs.client';

/** A page's draft, as the optimization that produced it names it. */
export interface ISuggestionOfPage {
  /** The revision of the draft: the output of the optimization that produced it. */
  suggestionRevisionId: InternalId;
  /** The key of the page: only the page's own optimizations are listed. */
  pageKey: string;
}

/** The page whose draft is asked for. */
export type IDraftOfPage = Pick<ISuggestionOfPage, 'pageKey'>;

/** What Sync Core answers the lookup of a page's draft with when the page has none. */
const NO_DRAFT_STATUS = 404;

/**
 * Whether an optimization's draft can still be the page's draft. An optimization names the revision it
 * wrote once it has written it. A draft Sync Core wrote and then applied to the site itself is retired
 * from the page when it is applied, while a draft the site wrote stays the page's draft after the site
 * published it.
 */
function mayHoldTheDraft(optimization: ClientContentOptimizationEntity): boolean {
  const namesItsOutput = !!(optimization.outputEntity?.getRevisionId() || optimization.outputPage?.getRevisionId());
  const appliedBySyncCore = optimization.status === ContentOptimizationStatus.Applied && !optimization.externalRevisionId;
  return namesItsOutput && !appliedBySyncCore;
}

/**
 * The page's draft revision, looked up by the page's key, or undefined when the page has none. A page
 * without a draft is the common case, and Sync Core answers its lookup with a not-found, which is read
 * as no draft; every other failure is the caller's.
 */
export async function loadDraftOfPage(api: ISyncCoreApi, { pageKey }: IDraftOfPage): Promise<ClientContentItemEntity | undefined> {
  try {
    return (await api.governance.contentItems.itemByKey(pageKey, ContentItemStatus.Suggestion)) ?? undefined;
  } catch (error) {
    if (httpStatusOfError(error) === NO_DRAFT_STATUS) {
      return undefined;
    }

    throw error;
  }
}

/**
 * The page's draft revision, or undefined when it has none, looked up only when the page can have one.
 * A draft is only ever written as the output of one of the page's optimizations, so a page none of whose
 * optimizations can still hold one has none: one never optimized, one whose optimization is still being
 * written, one whose only draft Sync Core applied. Such a page is not asked for its draft, which Sync
 * Core would answer with a not-found. When the page's optimizations cannot all be read, the draft is
 * looked up all the same.
 */
export async function loadDraftOfOptimizedPage(api: ISyncCoreApi, { pageKey }: IDraftOfPage): Promise<ClientContentItemEntity | undefined> {
  const optimizations = await loadPageOptimizations(api, pageKey);
  if (!optimizations.partial && !optimizations.items.some(mayHoldTheDraft)) {
    return undefined;
  }

  return loadDraftOfPage(api, { pageKey });
}

function isOptimizationOf(optimization: ClientContentOptimizationEntity, suggestionRevisionId: InternalId): boolean {
  return (
    optimization.outputEntity?.getRevisionId() === suggestionRevisionId || optimization.outputPage?.getRevisionId() === suggestionRevisionId
  );
}

/**
 * The optimization that produced a page's draft: the one whose output is the draft's revision, among
 * the optimizations of the page. One still open is looked for first (being written, awaiting the review,
 * or approved but not applied), then every other one, whichever state it reached.
 */
export async function loadOptimizationForSuggestion(
  api: ISyncCoreApi,
  { suggestionRevisionId, pageKey }: ISuggestionOfPage
): Promise<ClientContentOptimizationEntity | undefined> {
  const tryFind = async (statuses?: ContentOptimizationStatus[]) => {
    let page = 0;
    let numberOfPages = 1;

    while (page < numberOfPages) {
      const result = await api.governance.contentOptimizations.list(
        { page, itemsPerPage: 10 },
        {
          inputPageKey: pageKey,
          ...(statuses?.length ? { statuses } : {}),
        }
      );
      const match = (result.items || []).find((optimization) => isOptimizationOf(optimization, suggestionRevisionId));
      if (match) {
        return match;
      }

      numberOfPages = result.numberOfPages || 1;
      page += 1;
    }

    return undefined;
  };

  const preferredStatuses: ContentOptimizationStatus[] = [
    ContentOptimizationStatus.UserReview,
    ContentOptimizationStatus.Approved,
    ContentOptimizationStatus.Generating,
    ContentOptimizationStatus.SystemReview,
  ];
  const preferredMatch = await tryFind(preferredStatuses);
  if (preferredMatch) {
    return preferredMatch;
  }

  return tryFind();
}
